import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Mail, CheckCircle, AlertTriangle, RefreshCw, ExternalLink,
  Lock, Eye, EyeOff, Inbox, Send, Server, Key, Shield, Info,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import api from '@/services/api';

// ---------- helpers ----------

/**
 * Pull a human-readable error message out of an axios error.
 * Backend may send `detail` as a plain string OR as an object
 * `{stage, code, message}` (for IMAP/SMTP probe failures).
 */
function extractErrorMessage(err, fallback = 'Something went wrong.') {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object') {
    if (detail.message) {
      return detail.stage
        ? `${detail.stage.toUpperCase()} — ${detail.message}`
        : detail.message;
    }
  }
  return err?.message || fallback;
}

// ---------- IMAP / SMTP (fully wired) ----------

const PROVIDERS = [
  { name: 'Gmail', imap: 'imap.gmail.com', imap_port: '993', smtp: 'smtp.gmail.com', smtp_port: '587', notes: 'Requires Google App Password (2FA must be on).' },
  { name: 'Outlook / Hotmail', imap: 'outlook.office365.com', imap_port: '993', smtp: 'smtp.office365.com', smtp_port: '587', notes: 'For business accounts, IMAP may need to be enabled by your admin.' },
  { name: 'Yahoo', imap: 'imap.mail.yahoo.com', imap_port: '993', smtp: 'smtp.mail.yahoo.com', smtp_port: '465', notes: 'Requires Yahoo App Password.' },
  { name: 'iCloud', imap: 'imap.mail.me.com', imap_port: '993', smtp: 'smtp.mail.me.com', smtp_port: '587', notes: 'Requires Apple App-Specific Password.' },
];

const ImapSmtpSetup = ({ chatbotId, existingConnection, onSuccess }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [providerHint, setProviderHint] = useState(null);
  const [lastError, setLastError] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    imap_host: '',
    imap_port: '993',
    smtp_host: '',
    smtp_port: '587',
    username: '',
    password: '',
    use_ssl: true,
  });

  useEffect(() => {
    if (existingConnection?.imap?.connected) {
      setFormData(prev => ({
        ...prev,
        email: existingConnection.imap.email || '',
        imap_host: existingConnection.imap.imap_host || '',
        imap_port: String(existingConnection.imap.imap_port || '993'),
        smtp_host: existingConnection.imap.smtp_host || '',
        smtp_port: String(existingConnection.imap.smtp_port || '587'),
        username: existingConnection.imap.username || '',
        use_ssl: existingConnection.imap.use_ssl !== false,
      }));
    }
  }, [existingConnection]);

  const selectProvider = (p) => {
    setFormData(prev => ({
      ...prev,
      imap_host: p.imap,
      imap_port: p.imap_port,
      smtp_host: p.smtp,
      smtp_port: p.smtp_port,
      use_ssl: true,
    }));
    setProviderHint(p);
  };

  const validate = () => {
    if (!formData.email) return 'Email address is required.';
    if (!formData.imap_host) return 'IMAP server is required.';
    if (!formData.smtp_host) return 'SMTP server is required.';
    if (!formData.password) return 'Password (or App Password) is required.';
    return null;
  };

  const handleTest = async () => {
    setLastError(null);
    const v = validate();
    if (v) { toast.error(v); return; }
    setTesting(true);
    try {
      const { data } = await api.post(
        `/chatbots/${chatbotId}/channels/email/imap/test`,
        formData,
      );
      toast.success(data?.message || 'IMAP and SMTP both authenticated successfully.');
    } catch (err) {
      const msg = extractErrorMessage(err, 'Connection test failed.');
      setLastError(msg);
      toast.error(msg, { duration: 8000 });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    setLastError(null);
    const v = validate();
    if (v) { toast.error(v); return; }
    setSaving(true);
    try {
      await api.post(`/chatbots/${chatbotId}/channels/email/imap`, formData);
      toast.success('Email connected and credentials saved.');
      onSuccess?.();
    } catch (err) {
      const msg = extractErrorMessage(err, 'Failed to save email settings.');
      setLastError(msg);
      toast.error(msg, { duration: 8000 });
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect this email account?')) return;
    try {
      await api.delete(`/chatbots/${chatbotId}/channels/email/imap`);
      toast.success('Email disconnected');
      setFormData({
        email: '', imap_host: '', imap_port: '993', smtp_host: '', smtp_port: '587',
        username: '', password: '', use_ssl: true,
      });
      onSuccess?.();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Failed to disconnect email.'));
    }
  };

  return (
    <div className="space-y-6">
      {existingConnection?.imap?.connected && (
        <Alert className="bg-emerald-50 border-emerald-200">
          <CheckCircle className="w-4 h-4 text-emerald-600" />
          <AlertDescription className="text-emerald-800">
            Connected to <span className="font-medium">{existingConnection.imap.email}</span>.
            <Button size="sm" variant="link" className="text-emerald-700 ml-2 p-0 h-auto" onClick={handleDisconnect} data-testid="imap-disconnect-link">
              Disconnect
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {lastError && (
        <Alert className="bg-rose-50 border-rose-200">
          <AlertTriangle className="w-4 h-4 text-rose-600" />
          <AlertDescription className="text-rose-800 whitespace-pre-wrap" data-testid="imap-last-error">
            {lastError}
          </AlertDescription>
        </Alert>
      )}

      <div>
        <Label className="text-sm font-medium">Quick Setup — choose your provider</Label>
        <div className="flex flex-wrap gap-2 mt-2">
          {PROVIDERS.map(p => (
            <Button
              key={p.name}
              size="sm"
              variant={providerHint?.name === p.name ? 'default' : 'outline'}
              onClick={() => selectProvider(p)}
              data-testid={`provider-${p.name.toLowerCase().replace(/[^a-z]/g, '')}`}
            >
              {p.name}
            </Button>
          ))}
        </div>
        {providerHint && (
          <p className="text-xs text-slate-500 mt-2 flex items-start gap-1.5">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>{providerHint.notes}</span>
          </p>
        )}
      </div>

      <div className="grid gap-4">
        <div>
          <Label>Email Address *</Label>
          <Input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value, username: formData.username || e.target.value })}
            placeholder="your@email.com"
            className="mt-1"
            data-testid="email-input"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="flex items-center gap-1"><Inbox className="w-3 h-3" /> IMAP Server *</Label>
            <Input value={formData.imap_host} onChange={(e) => setFormData({ ...formData, imap_host: e.target.value })} placeholder="imap.example.com" className="mt-1" data-testid="imap-host-input" />
          </div>
          <div>
            <Label>IMAP Port</Label>
            <Input value={formData.imap_port} onChange={(e) => setFormData({ ...formData, imap_port: e.target.value })} placeholder="993" className="mt-1" data-testid="imap-port-input" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="flex items-center gap-1"><Send className="w-3 h-3" /> SMTP Server *</Label>
            <Input value={formData.smtp_host} onChange={(e) => setFormData({ ...formData, smtp_host: e.target.value })} placeholder="smtp.example.com" className="mt-1" data-testid="smtp-host-input" />
          </div>
          <div>
            <Label>SMTP Port</Label>
            <Input value={formData.smtp_port} onChange={(e) => setFormData({ ...formData, smtp_port: e.target.value })} placeholder="587" className="mt-1" data-testid="smtp-port-input" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="flex items-center gap-1"><Key className="w-3 h-3" /> Username</Label>
            <Input value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} placeholder="defaults to email" className="mt-1" data-testid="username-input" />
          </div>
          <div>
            <Label className="flex items-center gap-1"><Lock className="w-3 h-3" /> Password / App Password *</Label>
            <div className="relative mt-1">
              <Input
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="••••••••"
                className="pr-10"
                data-testid="password-input"
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Switch checked={formData.use_ssl} onCheckedChange={(v) => setFormData({ ...formData, use_ssl: v })} data-testid="use-ssl-toggle" />
          <Label className="text-sm">Use SSL/TLS encryption (recommended)</Label>
        </div>
      </div>

      <div className="flex items-center gap-3 pt-4 border-t">
        <Button variant="outline" onClick={handleTest} disabled={testing} data-testid="test-connection-btn">
          {testing ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Testing…</> : <><CheckCircle className="w-4 h-4 mr-2" /> Test Connection</>}
        </Button>
        <Button onClick={handleSave} disabled={saving} data-testid="save-imap-btn">
          {saving ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : 'Save Settings'}
        </Button>
      </div>

      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="py-4">
          <h4 className="font-medium text-amber-900 flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4" /> Common gotchas
          </h4>
          <ul className="text-sm text-amber-800 space-y-1">
            <li>• <strong>Gmail / Outlook with 2FA</strong>: you must use an <strong>App Password</strong>, not your normal password.
              <a className="ml-1 text-amber-900 underline inline-flex items-center gap-1" href="https://support.google.com/accounts/answer/185833" target="_blank" rel="noopener noreferrer">
                Google guide <ExternalLink className="w-3 h-3" />
              </a>
            </li>
            <li>• <strong>Port 993</strong> + SSL ON ✅ &nbsp; · &nbsp; <strong>Port 143</strong> + SSL OFF (STARTTLS handled automatically) ✅</li>
            <li>• <strong>Port 465</strong> SMTP needs SSL ON · <strong>Port 587</strong> uses STARTTLS (keep SSL toggle ON either way)</li>
            <li>• Credentials are encrypted at rest using the server&apos;s Fernet key — never logged.</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

// ---------- container ----------

const EmailChannelSetup = ({ chatbotId }) => {
  const [connection, setConnection] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/chatbots/${chatbotId}/channels/email`);
      setConnection(data);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Could not load email channel.'));
    } finally {
      setLoading(false);
    }
  }, [chatbotId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="email-channel-setup">
      <div>
        <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
          <Mail className="w-5 h-5 text-blue-600" /> Email Channel
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Connect an inbox so this AI Employee can read incoming emails as leads and auto-reply.
        </p>
      </div>

      {/* Gmail OAuth removed per product decision — IMAP/SMTP is the
          supported email path. Existing Gmail connections continue to
          work via the backend but no new ones can be created from the UI. */}
      <ImapSmtpSetup chatbotId={chatbotId} existingConnection={connection} onSuccess={load} />
    </div>
  );
};

export default EmailChannelSetup;
